import { createContext, ReactNode, useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { AxiosError, type AxiosInstance, type AxiosRequestConfig } from "axios";
import { useAuth } from "main";

type LaunchDarklyValue =
  | boolean
  | number
  | string
  | { [key: string]: LaunchDarklyValue }
  | LaunchDarklyValue[];

type FeatureFlags = {
  readonly [key: string]: LaunchDarklyValue;
};

type FeatureFlagContextType = {
  readonly flags: FeatureFlags;
  readonly isLoading: boolean;
  readonly error: AxiosError | null;
};

const FeatureFlagContext = createContext<FeatureFlagContextType | null>(null);

type FeatureFlagProviderProps = {
  api:
    | AxiosInstance
    | {
        (config: AxiosRequestConfig): Promise<{ data: unknown }>;
      }; // a simple mock object type to be used for testing / storybook
  readonly children: ReactNode;
};

export function FeatureFlagProvider(props: FeatureFlagProviderProps): JSX.Element {
  const { api, children } = props;
  const { status } = useAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: ["feature-flags", status],
    queryFn: async () => {
      const { data } = await api({ url: "/api/user/feature-flags" });
      return data;
    },
    /**
     * Only enable the query when AuthProvider has determined the auth status
     * This ensures the Authorization header is properly set (or not set) before making the request
     */
    enabled: status !== undefined,
    /**
     * Data is considered stale after 1 hour
     * Data stays in cache for 2 hours
     * Users see immediate data on mount (even if stale)
     * Fresh data is fetched in the background and cache updated
     * UI automatically updates when fresh data arrives
     * Memory is eventually cleaned up after 2 hours of inactivity
     */
    staleTime: 1 * 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
  });

  const value = {
    flags: (data?.data?.featureFlags ?? {}) as FeatureFlags,
    isLoading,
    error: error as AxiosError | null,
  };

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
}

export function useFeatureFlags(): FeatureFlagContextType {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error("useFeatureFlags must be used within a FeatureFlagProvider");
  }
  return context;
}
